// Road-snapping helpers.
// Single points (map click / hover preview) use OSRM /nearest — a tiny GET.
// Batches (loop via-points) use Valhalla /locate — one POST for all points.

import { withTimeout } from './fetchUtils';
import { nearestPointOnPolylines } from './geomath';
import { rlog, rwarn, rnow } from './routingLog';

const OSRM_BASE = 'https://router.project-osrm.org';
const VALHALLA_HOSTS = [
  'https://valhalla1.openstreetmap.de',
  'https://valhalla2.openstreetmap.de',
];

const HIGHWAY_CLASSES =
  'motorway|trunk|primary|secondary|tertiary|unclassified|residential|living_street|' +
  'motorway_link|trunk_link|primary_link|secondary_link|tertiary_link|service';

/**
 * Fetch drivable road geometry inside a bbox for local (client-side) snapping.
 * Returns an array of polylines: [[[lat,lng], ...], ...]. Empty on failure.
 *
 * This is called on map pans, so it must be gentle: ONE endpoint at a time
 * (kumi.systems first — it tolerates frequent queries far better than
 * overpass-api.de, whose rate limiter drops connections).
 */
const ROAD_GEOMETRY_ENDPOINTS = [
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
  'https://overpass-api.de/api/interpreter',
];

export async function fetchRoadGeometry(south, west, north, east, signal) {
  const q = `[out:json][timeout:8];way["highway"~"^(${HIGHWAY_CLASSES})$"](${south},${west},${north},${east});out geom;`;
  const t0 = rnow();
  for (const url of ROAD_GEOMETRY_ENDPOINTS) {
    const t1 = rnow();
    try {
      rlog(`snap: POST ${url} (road geometry for the visible area)`);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${encodeURIComponent(q)}`,
        // Tighter than the old 9s — this is a background hover-preview
        // enhancement, not a blocking action, so a slow mirror shouldn't be
        // allowed to eat up to 27s (3 mirrors × 9s) before giving up.
        signal: withTimeout(4000, signal),
      });
      if (!res.ok) { rwarn(`snap: ${url} → HTTP ${res.status} after ${Math.round(rnow() - t1)}ms, trying next mirror`); continue; }
      const data = await res.json();
      const lines = (data.elements || [])
        .filter(el => Array.isArray(el.geometry) && el.geometry.length > 1)
        .map(el => el.geometry.map(g => [g.lat, g.lon]));
      if (lines.length) {
        rlog(`snap: ${url} responded in ${Math.round(rnow() - t1)}ms — ${lines.length} road segments (total ${Math.round(rnow() - t0)}ms)`);
        return lines;
      }
      rwarn(`snap: ${url} returned zero road segments after ${Math.round(rnow() - t1)}ms — trying next mirror`);
      // empty result from this mirror — try the next one
    } catch (err) {
      if (err.name === 'AbortError' && signal?.aborted) { rlog('snap: aborted (map moved again)'); throw err; }
      rwarn(`snap: ${url} failed after ${Math.round(rnow() - t1)}ms (${err.message}) — trying next mirror`);
      // connection reset / timeout — try next endpoint
    }
  }
  rwarn(`snap: all ${ROAD_GEOMETRY_ENDPOINTS.length} Overpass mirrors failed after ${Math.round(rnow() - t0)}ms — no snap preview for this area`);
  return [];
}

// Major roads only — these are continuous, two-way through-routes, so snapping
// a loop via onto one avoids the cul-de-sac / dead-end spurs that force U-turns.
const MAIN_ROAD_CLASSES =
  'motorway|trunk|primary|secondary|tertiary|' +
  'motorway_link|trunk_link|primary_link|secondary_link|tertiary_link';

/**
 * Snap an array of {lat, lng, ...} points onto the nearest MAIN road.
 *
 * Fetches only the major roads WITHIN `radiusM` of each point (one Overpass
 * union query of `around:` clauses — tiny payload, fast), then snaps each point
 * locally. Points with no main road in range (or on Overpass failure) are
 * returned unchanged, so the caller can fall back to generic snapping.
 */
export async function snapPointsToMainRoads(points, { radiusM = 6000, signal } = {}) {
  if (!points.length) return points;

  const clauses = points
    .map(p => `way["highway"~"^(${MAIN_ROAD_CLASSES})$"](around:${radiusM},${p.lat.toFixed(5)},${p.lng.toFixed(5)});`)
    .join('');
  const q = `[out:json][timeout:12];(${clauses});out geom;`;

  let lines = [];
  for (const url of ROAD_GEOMETRY_ENDPOINTS) {
    const t1 = rnow();
    try {
      rlog(`snap-to-main-road: POST ${url} (${points.length} via-point(s))`);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${encodeURIComponent(q)}`,
        // Tighter than the old 12s — see the note in fetchRoadGeometry above.
        signal: withTimeout(5000, signal),
      });
      if (!res.ok) { rwarn(`snap-to-main-road: ${url} → HTTP ${res.status} after ${Math.round(rnow() - t1)}ms`); continue; }
      const data = await res.json();
      lines = (data.elements || [])
        .filter(el => Array.isArray(el.geometry) && el.geometry.length > 1)
        .map(el => el.geometry.map(g => [g.lat, g.lon]));
      if (lines.length) { rlog(`snap-to-main-road: ${url} responded in ${Math.round(rnow() - t1)}ms`); break; }
    } catch (err) {
      if (err.name === 'AbortError' && signal?.aborted) throw err;
      rwarn(`snap-to-main-road: ${url} failed after ${Math.round(rnow() - t1)}ms (${err.message})`);
    }
  }
  if (!lines.length) { rwarn('snap-to-main-road: no main road found nearby — points left unsnapped'); return points; }

  return points.map(p => {
    const snapped = nearestPointOnPolylines(p.lat, p.lng, lines, radiusM);
    return snapped ? { ...p, lat: snapped.lat, lng: snapped.lng } : p;
  });
}

/**
 * Snap a single point to the nearest road.
 * Returns { lat, lng, roadName } or null if no road within maxDistM.
 */
export async function snapToRoad(lat, lng, { maxDistM = 500, signal } = {}) {
  const t0 = rnow();
  try {
    const res = await fetch(
      `${OSRM_BASE}/nearest/v1/driving/${lng},${lat}?number=1`,
      { signal: withTimeout(3000, signal) }
    );
    if (!res.ok) { rwarn(`snapToRoad: OSRM /nearest → HTTP ${res.status} after ${Math.round(rnow() - t0)}ms`); return null; }
    const data = await res.json();
    const w = data?.waypoints?.[0];
    if (!w || w.distance > maxDistM) { rlog(`snapToRoad: nothing within ${maxDistM}m (${Math.round(rnow() - t0)}ms)`); return null; }
    return { lat: w.location[1], lng: w.location[0], roadName: w.name || '' };
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    rwarn(`snapToRoad: OSRM /nearest failed after ${Math.round(rnow() - t0)}ms (${err.message})`);
    return null;
  }
}

/**
 * Snap an array of {lat, lng, ...} points to the road network in ONE request.
 * Unsnappable points are returned unchanged. Extra fields are preserved.
 */
export async function snapPointsToRoads(points, signal) {
  if (!points.length) return points;
  const body = JSON.stringify({
    locations: points.map(p => ({ lat: p.lat, lon: p.lng, radius: 3000 })),
    costing: 'auto',
    verbose: false,
  });

  // Race both hosts with a short cap — snapping is an enhancement, not a
  // requirement, so on failure we return the points unchanged quickly.
  const t0 = rnow();
  rlog(`snapPointsToRoads: racing Valhalla /locate for ${points.length} point(s)`);
  try {
    const data = await Promise.any(
      VALHALLA_HOSTS.map(async host => {
        const res = await fetch(`${host}/locate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          signal: withTimeout(4000, signal),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!Array.isArray(json)) throw new Error('Bad response');
        return json;
      })
    );
    rlog(`snapPointsToRoads: done in ${Math.round(rnow() - t0)}ms`);
    return points.map((p, i) => {
      const edge = data[i]?.edges?.[0];
      return edge?.correlated_lat != null
        ? { ...p, lat: edge.correlated_lat, lng: edge.correlated_lon }
        : p;
    });
  } catch (err) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    rwarn(`snapPointsToRoads: both Valhalla hosts failed after ${Math.round(rnow() - t0)}ms (${err?.errors?.map(e => e.message).join(', ')}) — points left unsnapped`);
    return points;
  }
}
